import React, {useState, useEffect} from "react";
import {Link, useNavigate} from 'react-router-dom';
import {Card, Badge, Modal, Button, Form} from 'react-bootstrap';
import {apiClient} from '../api/ApiClient';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import './EmailComponents.css';
import '../fastPhotos/FastPhotosComponent.css';
import EmailActions from './EmailActions';
import {Trash2, User } from 'lucide-react';
import PaginationComponent from "../pagingResults/PaginationComponent";


const InboxComponent = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [errorEmails, setErrorEmails] = useState(null);
  const {fastPhotos, loading, error} = useFastPhotos();

  const [showModal, setShowModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [replyText, setReplyText] = useState('');

  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Function to record profile view
  const recordProfileView = async (memberId) => {
    try {
      console.log('Recording view for profile ID:', memberId);
      
      const response = await apiClient.post('/rest/recordView', {
        viewedMemberId: memberId
      });
      
      console.log('View recorded response:', response.data);
    } catch (error) {
      console.error('Error recording profile view:', error);
    }
  };

  // Handle navigating to a profile
  const handleProfileView = (e, memberId) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('token');
    if (token) {
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  // Handle click on a close match profile
  const handleCloseMatchClick = (e, photo) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(photo[0]);
      // Navigate to the profile page
      navigate(`/profile/view/1/${photo[0]}`);
    } else {
      navigate('/login');
    }
  };

  const fetchEmails = async () => {
    setLoadingEmails(true);
    try {
      // Use the new paginated endpoint
      const response = await apiClient.get(`/rest/email/inbox?page=${currentPage}&size=${itemsPerPage}`);
      
      if (response.status === 200) {
       // console.log('Inbox emails response:', response.data);
        
        // Handle Spring Boot Page object
        if (response.data.content) {
          // If the response is a Page object
          setEmails(response.data.content);
          setTotalElements(response.data.totalElements);
        } else {
          // Fallback for backward compatibility
          const validData = response.data.filter(item => Array.isArray(item) && item.length > 1);
          setEmails(validData);
          setTotalElements(validData.length);
        }
      } else {
        setErrorEmails('Failed to fetch inbox emails');
      }
    } catch (error) {
      console.error('Error fetching inbox emails:', error);
      setErrorEmails('Error fetching inbox emails. Please try again.');
    } finally {
      setLoadingEmails(false);
    }
  };

  useEffect(() => {
    console.log('Fetching inbox emails - Page:', currentPage, 'Size:', itemsPerPage);
    fetchEmails();
  }, [currentPage, itemsPerPage]);

  const handleReportMember = async (memberId) => {
    try {
      await apiClient.post(`/rest/report/${memberId}`);
      return true;
    } catch (error) {
      console.error('Error reporting member:', error);
      return false;
    }
  };

  const formatDateTime = (dateString) => {
    const options = {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  const handleBlockMember = async (memberId) => {
    try {
      await apiClient.post(`/rest/block/${memberId}`);
      return true;
    } catch (error) {
      console.error('Error blocking member:', error);
      return false;
    }
  };

  const handleDeleteEmail = async (emailId) => {
    try {
      const response = await apiClient.post('/rest/deleteMail', {
        emailIds: [emailId.toString()],
        memberId: null
      });

      if (response.status === 200) {
        setEmails(prevEmails => prevEmails.filter(email => email[0] !== emailId));
      }
    
      
      return response.status === 200;
    } catch (error) {
      console.error('Error deleting email:', error);
      return false;
    }
  };


  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEmail(null);
    setReplyText('');
  };

  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
  };

  const handleReplySubmit = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const emailData = {
      emailToMemberId: selectedEmail[1], // Use appropriate index based on your email data structure
      to: selectedEmail[2], // Use appropriate index based on your email data structure
      subject: selectedEmail[3], // Use appropriate index based on your email data structure
      body: replyText,
      from: "info@netfixup.com" // Or the appropriate 'from' email
    };

    apiClient.post('/rest/sendmail', emailData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (response.status === 200) {
          alert('Reply sent successfully!');
          handleCloseModal();
        } else {
          alert('Failed to send reply. Please try again.');
        }
      })
      .catch(error => {
        console.error('Error sending reply:', error);
        alert('Failed to send reply. Please try again.');
      });
  };

  const buttonStyle = {
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',  // Space between icon and text
    padding: '8px 16px',  // Increased padding
    minWidth: '100px'  // Minimum width for consistency
  };

  const containerStyle = {
    display: 'flex',
    gap: '15px'  // Increased gap between buttons
  };

  if (loadingEmails || loading) return <p>Loading...</p>;
  if (errorEmails) return <p>{errorEmails}</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="container-1024">
      <div className="results-area">
       <h3>My Inbox</h3>
        <div className="results-content">

          <div className="results-center-details">
            <div className="container d-flex flex-wrap justify-content-left">
              {emails.length > 0 ? (

                <>
                  <div className="w-100 mb-3">  {/* Changed from mt-3 to mb-3 for margin-bottom */}
                    <PaginationComponent
                      totalItems={totalElements}
                      itemsPerPage={itemsPerPage}
                      currentPage={currentPage}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(newSize) => {
                        setItemsPerPage(newSize);
                        setCurrentPage(0); // Reset to first page when changing size
                      }}
                    />
                  </div>

                  {emails.map((email, index) => (
                    <Card key={index} className="favorite-card position-relative">
                      <div className="delete-icon-wrapper"
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: 1,
                          cursor: 'pointer',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '50%',
                          padding: '5px'
                        }}>
                        <Trash2
                          size={20}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteEmail(email[0]);
                          }}
                        />
                      </div>
                      <Link onClick={(e) => {
                        e.preventDefault();
                        handleEmailClick(email);
                      }} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <Card.Img
                          variant="top"
                          src={`https://pics.netfixup.com/${email[18]}/${email[17]}`}
                          alt={email[18]}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                          }}
                        />
                        <Card.Body>
                          <Card.Title>{email[2]}</Card.Title>
                          <Card.Text>
                            <b>Subject:</b> {email[3]}<br />
                            <b>Body:</b> {truncateText(email[4], 75)}<br />
                            <b>Date:</b> {email[6]} - {email[7]}
                          </Card.Text>
                        </Card.Body>
                      </Link>
                    </Card>
                  ))}
                </>
              ) : (
                <p>No Emails Found</p>
              )}
            </div>
          </div>

          <div className="results-right-sidebar">
            <h5>Close Matches</h5>
            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>{error}</p>
            ) : fastPhotos.length > 0 ? (
              fastPhotos.map((photo, index) => (
                <Card key={index} className="fast-photo-card-side">
                  {/* Changed from Link to anchor with onClick handler */}
                  <a 
                    href={`/profile/view/1/${photo[0]}`}
                    onClick={(e) => handleCloseMatchClick(e, photo)}
                  >
                    <Card.Img 
                      variant="top" 
                      src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                      }}
                    />
                  </a>
                  <Badge pill bg="light" text="dark">
                    {photo[2]}
                  </Badge>
                </Card>
              ))
            ) : (
              <p>No Fast Photos Found</p>
            )}
          </div>
        </div>
      </div>


      {selectedEmail && (
        <Modal show={showModal} onHide={handleCloseModal} centered className="custom-modal" size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{selectedEmail[2]}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="modal-body-custom">
            <div className="modal-content-custom">
              <div className="modal-image">
                <img
                  src={`https://pics.netfixup.com/${selectedEmail[18]}/${selectedEmail[17]}`}
                  alt={selectedEmail[18]}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                  }}
                />
              </div>
              <div className="modal-details">
                <div className="email-details">
                  <p><b>Subject:</b> {selectedEmail[3]}</p>
                  <p><b>Date:</b> {selectedEmail[6]} - {selectedEmail[7]} </p>
                  <p><b>Body:</b> {selectedEmail[14]}</p>
                </div>
                <div className="reply-section">
                  <Form.Control
                    as="textarea"
                    value={replyText}
                    onChange={handleReplyChange}
                    placeholder="Type your reply here..."
                    className="reply-textarea"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <div className="d-flex justify-content-between align-items-center w-100">
              {/* Added Profile View Button to the left side of the footer */}
              <Button 
                variant="outline-primary" 
                onClick={(e) => {
                  recordProfileView(selectedEmail[1]);
                  handleProfileView(e, selectedEmail[1]);
                  handleCloseModal();
                }}
              >
                <User size={16} className="me-1" /> View Profile
              </Button>
              
              <div className="d-flex align-items-center" style={{gap: '15px'}}>
                <EmailActions
                  memberId={selectedEmail[1]}
                  emailId={selectedEmail[0]}
                  onBlock={async (memberId) => {
                    const success = await handleBlockMember(memberId);
                    if (success) {
                      handleCloseModal();
                    }
                  }}
                  onReport={async (memberId) => {
                    const success = await handleReportMember(memberId);
                    if (success) {
                      handleCloseModal();
                    }
                  }}
                  onDelete={async (emailId) => {
                    const success = await handleDeleteEmail(emailId);
                    if (success) {
                      setEmails(prevEmails => prevEmails.filter(email => email[0] !== emailId));
                      handleCloseModal();
                    }
                  }}
                />

                <Button variant="secondary" onClick={handleCloseModal} style={{minWidth: '100px'}}>
                  Close
                </Button>
                <Button variant="primary" onClick={handleReplySubmit} style={{minWidth: '100px'}}>
                  Send Reply
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default InboxComponent;